import { useState, useCallback, useRef, act, useEffect } from "react";
import { useQuery } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { listDayCenter } from "../../../api/server";
import { setPage, updateRecords } from "../dayCenterSlice";
import { useParams } from "react-router-dom";
import debounce from "lodash.debounce";

const useDayCenterData = (activeDate, activeBranch) => {
  const [changes, setChanges] = useState({});
  const dispatch = useDispatch();
  const dayCenterPage = useSelector((state) => state.dayCenter.page);
  let { branchId } = useParams();
  const branch = branchId ? branchId : activeBranch;
  const scopePrefix = `branches/${branch}/`;
  const { current, pageSize, total, filter, order } = dayCenterPage;
  const { freeText } = filter;
  const { isLoading, data, refetch } = useQuery(
    [
      current,
      pageSize,
      total,
      freeText,
      branch,
      order,
      activeBranch,
      activeDate,
    ],
    async () => {
      const result = await listDayCenter({
        ...dayCenterPage,
        scopePrefix,
        query: {
          date: activeDate,
        },
      });
      dispatch(setPage({ ...dayCenterPage, total: result.data.total }));
      return result.data.items;
    },
    {
      keepPreviousData: true,
      staleTime: 0,
      enabled: branch !== null && branch !== undefined,
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    if (branch !== null && branch !== undefined) {
      refetch();
    }
  }, [activeBranch, refetch]);

  useEffect(() => {
    const initialChanges = data
      ? data.reduce((acc, item) => {
          const key = `${item.patient_id}_${item.order_id}`;
          acc[key] = {
            ...item,
            approved: item?.event?.id !== undefined,
            day_center_approved_info:
              !item?.day_center_approved_info && item?.event?.id
                ? 1
                : !item?.day_center_approved_info && !item?.event?.id
                ? 4
                : item?.day_center_approved_info,
            date: item?.event?.date || activeDate,
            comment: item?.comment || "",
            day_center_half_day: item?.day_center_half_day || false,
          };
          return acc;
        }, {})
      : {};

    setChanges(initialChanges); // Reset changes with new data
  }, [data]);

  const onTextChange = useRef(
    debounce((value) => {
      dispatch(
        setPage({
          ...dayCenterPage,
          filter: {
            ...dayCenterPage.filter,
            freeText: value,
          },
        })
      );
    }, 500)
  ).current;

  const handleChanges = useCallback(
    (patientId, orderId, field, value) => {
      setChanges((prevChanges) => {
        const key = `${patientId}_${orderId}`;
        const updatedChanges = {
          ...prevChanges,
          [key]: {
            ...prevChanges[key],
            [field]: value,
          },
        };
        return updatedChanges;
      });
    },
    [activeDate]
  );

  const updateServer = useCallback(async () => {
    try {
      const events = Object.values(changes)
        .filter((change) => {
          const originalEvent = data.find(
            (item) =>
              item.patient_id === change.patient_id &&
              item.order_id === change.order_id
          );
          return (
            change.approved !== originalEvent?.approved ||
            change.comment !== originalEvent?.comment ||
            change.day_center_half_day !== originalEvent?.day_center_half_day ||
            change.day_center_approved_info !==
              originalEvent?.day_center_approved_info
          );
        })
        .map((change) => ({
          date: change.date || activeDate,
          patient_id: change.patient_id,
          order_id: change.order_id,
          approved: change.approved,
          day_center_approved_info: change.day_center_approved_info,
          event_id: change.event_id,
          comment: change.comment,
          day_center_half_day: change.day_center_half_day,
        }));

      await dispatch(updateRecords({ events, scopePrefix }));
      refetch(); // Refetch data to get the latest state from the server
    } catch (error) {
      console.error("Error updating patients:", error);
    }
  }, [changes, activeDate, dispatch, scopePrefix, refetch, data]);

  const onChange = useCallback(
    ({ current, pageSize }, filters, { field, order }, extra) => {
      const { textFilter } = dayCenterPage?.filter || {};
      const pageNumber = extra["action"] === "paginate" ? current : 1;

      const page = {
        current: pageNumber,
        pageSize,
        filter: {
          ...filters,
          freeText: textFilter,
        },
        order: {
          field,
          order,
        },
      };

      dispatch(setPage(page));
    },
    [dayCenterPage?.filter, dispatch]
  );
  return {
    changes,
    isLoading,
    data,
    refetch,
    handleChanges,
    updateServer,
    onChange,
    onTextChange,
  };
};

export default useDayCenterData;
